import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "../lib/api";

export default function PaymentResult() {
  const { result } = useParams();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(result === "cancel" ? "cancelled" : "checking");
  const [planInfo, setPlanInfo] = useState(null);
  const returnTo = localStorage.getItem("timely_pay_return") || "/";

  useEffect(() => {
    if (result !== "success") return;
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPlanInfo(data);
          setStatus("paid");
          return;
        }
      } catch { /* keep polling */ }
      attempts += 1;
      if (attempts < 20) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [result, params]);

  const activeMessage = planInfo
    ? planInfo.mode === "one_time"
      ? `Your access is active for the next ${planInfo.duration_years || 1} year${(planInfo.duration_years || 1) > 1 ? "s" : ""} — no renewal needed.`
      : "Your subscription is active for the next 12 months."
    : "Your subscription is active.";

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="payment-result-page">
      <div className="nb-card p-10 max-w-md w-full text-center bg-white">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-zinc-400" />
            <h1 className="font-display font-black text-2xl mt-5" data-testid="payment-status-heading">Confirming payment…</h1>
            <p className="text-zinc-500 text-sm mt-2">Just a moment while Stripe confirms.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-xl border-2 border-zinc-900 bg-[#A7E4D3] flex items-center justify-center shadow-[4px_4px_0px_0px_#18181B]">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h1 className="font-display font-black text-2xl mt-5" data-testid="payment-status-heading">Payment confirmed!</h1>
            <p className="text-zinc-500 text-sm mt-2">{activeMessage}</p>
            <Link to={returnTo} data-testid="payment-return-btn" className="nb-btn bg-[#1E3A8A] text-white px-6 py-3 mt-6 inline-flex">Back to my dashboard</Link>
          </>
        )}
        {status === "cancelled" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-xl border-2 border-zinc-900 bg-[#FDE68A] flex items-center justify-center shadow-[4px_4px_0px_0px_#18181B]">
              <XCircle className="w-8 h-8" />
            </div>
            <h1 className="font-display font-black text-2xl mt-5" data-testid="payment-status-heading">Checkout cancelled</h1>
            <p className="text-zinc-500 text-sm mt-2">No charge was made. You can subscribe whenever you're ready.</p>
            <Link to={returnTo} data-testid="payment-return-btn" className="nb-btn bg-white px-6 py-3 mt-6 inline-flex">Go back</Link>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <h1 className="font-display font-black text-2xl mt-5" data-testid="payment-status-heading">Still processing</h1>
            <p className="text-zinc-500 text-sm mt-2">We couldn't confirm the payment yet. If you completed checkout, it will activate shortly.</p>
            <Link to={returnTo} data-testid="payment-return-btn" className="nb-btn bg-white px-6 py-3 mt-6 inline-flex">Go back</Link>
          </>
        )}
      </div>
    </div>
  );
}
